/**
 * Vaults Page - Manage collateral vaults
 */

import { useState, useEffect, Fragment } from 'react'
import { useAccount, useChainId, useSwitchChain, useBalance } from 'wagmi'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { mainnet } from 'wagmi/chains'
import { Dialog, Transition } from '@headlessui/react'
import { SEO, generateBreadcrumbSchema } from '../components/common/SEO'
import { Card, CardContent } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { useDepositToAccount, useWithdrawFromAccount, useAccountCollateralBalance } from '../hooks/useVault'
import { useUserLOCsFromEvents } from '../hooks/useLOCEvents'
import { useApproveToken } from '../hooks/useTokenApproval'
import { useWrapETH } from '../hooks/useWrapETH'
import { formatAddress, formatTokenAmount } from '../utils/format'
import { toast } from 'react-hot-toast'
import { VaultIcon } from '../assets/icons'
import { InfoBox } from '../components/common/InfoBox'
import { Input } from '../components/common/Input'
import { WalletConnectIllustration } from '@bitcoin-com/react-components/src/components/mini-illustrations'
import { TransactionHistory } from '../components/vault/TransactionHistory'
import { AssetListItem } from '../components/vault/AssetListItem'
import { CryptoAssetsList as _CryptoAssetsList } from '../components/vault/CryptoAssetsList'
import { LiquidationRiskIndicator } from '../components/vault/LiquidationRiskIndicator'
import { useTokenPrices } from '../hooks/useTokenPrice'
import { useTransactionHistory } from '../hooks/useTransactionHistory'
import { TokenSelector } from '../components/vault/TokenSelector'
import { HowItWorksFlow, StepProgress } from '../components/common/HowItWorksFlow'
import { AmountInput } from '../components/vault/AmountInput'
import { MINIMUM_COLLATERAL_USD } from '../config/constants'
import { StepIndicator } from '../components/vault/StepIndicator'
import { BTCDepositModal } from '../components/vault/BTCDepositModal'
import { BTCDepositHistory } from '../components/vault/BTCDepositHistory'
import { VaultDetailsModal } from '../components/vault/VaultDetailsModal'
import { SUPPORTED_TOKENS, Token, parseTokenAmount } from '@contracts/tokens'
import { getContractAddress } from '@contracts/addresses'
import { useReadContract } from 'wagmi'
import { ERC20ABI } from '@contracts/abis'
import type { VaultAsset as _VaultAsset } from '../components/vault/CryptoAssetsList'

export function VaultsPage() {
  const { address, isConnected, chain } = useAccount()
  useChainId()
  const { switchChain: _switchChain } = useSwitchChain()
  const { openConnectModal } = useConnectModal()
  const { deposit, isPending, isSuccess, isError, error, hash } = useDepositToAccount()
  const { withdraw, isPending: isWithdrawPending, isSuccess: isWithdrawSuccess, isError: isWithdrawError, error: withdrawError, hash: withdrawHash } = useWithdrawFromAccount()
  const { approve: approveToken, isPending: isApproving, isSuccess: isApproved } = useApproveToken()
  const { wrapETH, isPending: isWrapping, isSuccess: isWrapSuccess, hash: wrapHash } = useWrapETH()
  
  const [showDepositModal, setShowDepositModal] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showBTCDepositModal, setShowBTCDepositModal] = useState(false)
  const [showBTCHistoryModal, setShowBTCHistoryModal] = useState(false)
  const [showVaultDetailsModal, setShowVaultDetailsModal] = useState(false)
  const [depositedTokenSymbol, setDepositedTokenSymbol] = useState<string>('')
  const [depositedAmount, setDepositedAmount] = useState<string>('')
  const [selectedVaultId, setSelectedVaultId] = useState<bigint | null>(0n) // Default expanded
  
  // Deposit form state
  const [selectedToken, setSelectedToken] = useState<Token | undefined>()
  const [depositAmount, setDepositAmount] = useState('')
  const [depositStep, setDepositStep] = useState<1 | 2 | 3>(1) // 1: Wrap, 2: Approve, 3: Deposit
  const [hasApprovedCurrentAmount, setHasApprovedCurrentAmount] = useState(false) // Bridge gap between approval confirm and allowance refetch
  
  // Withdraw form state
  const [withdrawToken, setWithdrawToken] = useState<Token | undefined>()
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [withdrawConfirmation, setWithdrawConfirmation] = useState('')
  
  // Check for active LOCs (use event-based query since contract lacks getUserLOCs)
  const { locs: locEvents } = useUserLOCsFromEvents(address)
  const hasActiveLOCs = (locEvents?.length || 0) > 0
  
  // Note: Anvil uses account-based collateral, not vault IDs
  // For now, we'll show a simplified UI
  const hasAccount = !!address
  const isLoading = false // TODO: Implement actual account balance checking
  
  // Get user's ETH balance (for native ETH)
  const { data: ethBalance, refetch: refetchEthBalance } = useBalance({
    address,
    query: {
      enabled: !!address && selectedToken?.isNative,
    },
  })
  
  // Get user's token balance (for ERC20 tokens)
  const { data: tokenBalance, refetch: refetchTokenBalance } = useReadContract({
    address: selectedToken?.address,
    abi: ERC20ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && !!selectedToken && !selectedToken.isNative,
    },
  })
  
  // Determine the actual balance to use
  const actualBalance = selectedToken?.isNative 
    ? ethBalance?.value 
    : (tokenBalance as bigint | undefined)
  
  // Get current allowance (not needed for native ETH)
  const { data: currentAllowance, refetch: refetchAllowance } = useReadContract({
    address: selectedToken?.address,
    abi: ERC20ABI,
    functionName: 'allowance',
    args: address ? [address, getContractAddress('CollateralVault')] : undefined,
    query: {
      enabled: !!address && !!selectedToken && !selectedToken.isNative,
    },
  })
  
  // Get available balance for withdrawal
  const { availableBalance: withdrawAvailableBalance } = useAccountCollateralBalance(
    address,
    withdrawToken?.address
  )
  
  // Calculate if approval is needed
  const depositAmountBigInt = selectedToken && depositAmount
    ? parseTokenAmount(depositAmount, selectedToken.decimals)
    : 0n
  
  // Track whether allowance data has loaded
  const isAllowanceLoading = !!address && !!selectedToken && !selectedToken.isNative && currentAllowance === undefined
  
  // Native ETH doesn't need approval (will be wrapped)
  // ERC20 tokens need approval if current allowance is less than deposit amount
  // If we just approved, skip the check until allowance refetch completes
  const needsApproval = hasApprovedCurrentAmount
    ? false
    : selectedToken && !selectedToken.isNative && depositAmount && depositAmountBigInt > 0n
      ? (currentAllowance === undefined || (currentAllowance as bigint) < depositAmountBigInt)
      : false
  
  // Fetch real transaction history from blockchain
  const { transactions, isLoading: _isLoadingHistory } = useTransactionHistory(address)
  
  // Calculate total collateral value for liquidation risk
  const depositableTokens = Object.values(SUPPORTED_TOKENS).filter(t => !t.isNative)
  
  // Fetch token prices for risk calculation
  const { data: prices } = useTokenPrices(depositableTokens)
  const tokenBalancesForRisk = depositableTokens.map(token => {
    const { availableBalance, reservedBalance } = useAccountCollateralBalance(
      address,
      token.address
    )
    const totalBalance = (availableBalance || 0n) + (reservedBalance || 0n)
    const formattedBalance = formatTokenAmount(totalBalance, token.decimals)
    const price = prices?.[token.symbol] || 0
    const valueUSD = parseFloat(formattedBalance) * price
    
    return { valueUSD, hasBalance: totalBalance > 0n }
  })
  
  const totalCollateralValueUSD = tokenBalancesForRisk.reduce((sum, b) => sum + (b.hasBalance ? b.valueUSD : 0), 0)

  // Calculate actual LOC value from event data instead of assuming 50%
  const estimatedLOCValueUSD = (() => {
    if (!hasActiveLOCs || !locEvents || !prices) return 0
    return locEvents.reduce((sum, loc) => {
      const creditedToken = Object.values(SUPPORTED_TOKENS).find(
        t => t.address.toLowerCase() === loc.creditedTokenAddress.toLowerCase()
      )
      if (!creditedToken) return sum
      const amount = parseFloat(formatTokenAmount(loc.creditedTokenAmount, creditedToken.decimals))
      const price = prices[creditedToken.symbol] || 0
      return sum + amount * price
    }, 0)
  })()
  
  // Calculate user progress through the flow
  const userProgress: StepProgress = {
    fundVault: totalCollateralValueUSD >= MINIMUM_COLLATERAL_USD, // Has deposited sufficient collateral
    issueLOC: hasActiveLOCs, // Has issued at least one LOC
    applyLoan: false, // TODO: Track loan requests (for now false)
    loanStatus: undefined // TODO: Track loan status
  }
  
  // Check if on correct network
  void (isConnected && chain?.id !== mainnet.id)

  // Handle successful deposit
  useEffect(() => {
    if (isSuccess && hash) {
      const isEthFlow = selectedToken?.symbol === 'WETH'
      toast.success(isEthFlow ? '✅ Step 3 Complete: Deposit successful!' : '✅ Deposit successful!')
      
      // Store deposit info for success modal
      setDepositedTokenSymbol(selectedToken?.symbol || '')
      setDepositedAmount(depositAmount)
      
      // Refetch balances
      setTimeout(() => {
        refetchTokenBalance()
        refetchEthBalance()
      }, 2000)
      
      // Close deposit modal and show success modal
      setShowDepositModal(false)
      setShowSuccessModal(true)
    }
  }, [isSuccess, hash, selectedToken, depositAmount, refetchTokenBalance, refetchEthBalance])

  // Handle deposit error
  useEffect(() => {
    if (isError && error) {
      toast.error(`Failed to deposit: ${error.message}`)
    }
  }, [isError, error])
  
  // Handle successful withdraw
  useEffect(() => {
    if (isWithdrawSuccess && withdrawHash) {
      toast.success('Withdrawal successful!')
      setShowWithdrawModal(false)
    }
  }, [isWithdrawSuccess, withdrawHash])

  // Handle withdraw error
  useEffect(() => {
    if (isWithdrawError && withdrawError) {
      toast.error(`Failed to withdraw: ${withdrawError.message}`)
    }
  }, [isWithdrawError, withdrawError])
  
  // Handle successful ETH wrap
  useEffect(() => {
    if (isWrapSuccess && wrapHash) {
      toast.success('✅ Step 1 Complete: ETH wrapped to WETH!')
      
      // Refetch balances
      setTimeout(() => {
        refetchEthBalance()
        refetchTokenBalance()
      }, 2000)
      
      // Auto-switch to WETH and advance to step 2
      setSelectedToken(SUPPORTED_TOKENS.WETH)
      setDepositStep(2)
    }
  }, [isWrapSuccess, wrapHash, refetchEthBalance, refetchTokenBalance])
  
  // Handle successful approval
  useEffect(() => {
    if (isApproved) {
      const isEthFlow = selectedToken?.symbol === 'WETH'
      toast.success(isEthFlow ? '✅ Step 2 Complete: WETH approved!' : '✅ Token approved!')
      
      // Immediately mark as approved so button switches to Deposit
      setHasApprovedCurrentAmount(true)
      
      // Refetch allowance from chain (no delay — tx is already confirmed)
      refetchAllowance()
      
      // Advance to step 3
      setDepositStep(3)
    }
  }, [isApproved, refetchAllowance, selectedToken])
  
  // Handle token approval
  const handleApprove = async () => {
    if (!selectedToken || !depositAmountBigInt) {
      toast.error('Please select a token and enter an amount')
      return
    }
    
    try {
      console.log('🔵 [VaultsPage] Approving token:', selectedToken.symbol)
      await approveToken(
        selectedToken.address,
        getContractAddress('CollateralVault'),
        depositAmountBigInt
      )
      toast.success(`${selectedToken.symbol} approved!`)
    } catch (err: any) {
      console.error('🔴 [VaultsPage] Approval error:', err)
      toast.error(`Approval failed: ${err.message || 'Unknown error'}`)
    }
  }
  
  // Handle deposit (with ETH wrapping if needed)
  const handleDeposit = async () => {
    if (!address || !selectedToken || !depositAmount || Number(depositAmount) <= 0) {
      toast.error('Please select a token and enter a valid amount')
      return
    }
    
    // Handle native ETH: wrap to WETH first
    if (selectedToken.isNative) {
      try {
        console.log('🔵 [VaultsPage] Wrapping ETH to WETH:', depositAmount)
        toast('Wrapping ETH to WETH...', { icon: '🔄' })
        
        await wrapETH(depositAmountBigInt)
        
        // After wrapping, the user will have WETH which they can approve and deposit
        toast.success('ETH wrapped to WETH! Now approve and deposit WETH.')
        
        // Switch to WETH token for next step
        setSelectedToken(SUPPORTED_TOKENS.WETH)
        return
      } catch (err: any) {
        console.error('🔴 [VaultsPage] Wrap error:', err)
        toast.error(`Failed to wrap ETH: ${err.message || 'Unknown error'}`)
        return
      }
    }
    
    // Handle ERC20 tokens
    if (needsApproval) {
      toast.error('Please approve the token first')
      return
    }
    
    try {
      console.log('🔵 [VaultsPage] Depositing:', depositAmount, selectedToken.symbol)
      
      await deposit(
        address, // account address
        [selectedToken.address], // token addresses
        [depositAmountBigInt] // amounts
      )
      
      console.log('🟢 [VaultsPage] Deposit successful!')
      
    } catch (err: any) {
      console.error('🔴 [VaultsPage] Deposit error:', err)
      toast.error(`Deposit failed: ${err.message || 'Unknown error'}`)
    }
  }
  
  // Reset approval tracking when token or amount changes
  useEffect(() => {
    setHasApprovedCurrentAmount(false)
  }, [selectedToken?.address, depositAmount])
  
  // Reset deposit form when modal closes
  useEffect(() => {
    if (!showDepositModal) {
      setSelectedToken(undefined)
      setDepositAmount('')
      setDepositStep(1)
      setHasApprovedCurrentAmount(false)
    }
  }, [showDepositModal])
  
  // Reset withdraw form when modal closes
  useEffect(() => {
    if (!showWithdrawModal) {
      setWithdrawToken(undefined)
      setWithdrawAmount('')
      setWithdrawConfirmation('')
    }
  }, [showWithdrawModal])
  
  // Handle withdraw
  const handleWithdraw = async () => {
    if (!address || !withdrawToken || !withdrawAmount || Number(withdrawAmount) <= 0) {
      toast.error('Please select a token and enter a valid amount')
      return
    }
    
    // Check confirmation if there are active LOCs
    if (hasActiveLOCs && withdrawConfirmation.trim().toLowerCase() !== 'i agree') {
      toast.error('Please type "I agree" to confirm withdrawal with active LOCs')
      return
    }
    
    const withdrawAmountBigInt = parseTokenAmount(withdrawAmount, withdrawToken.decimals)
    
    try {
      console.log('🔵 [VaultsPage] Withdrawing:', withdrawAmount, withdrawToken.symbol)
      
      // Withdraw function signature: (tokenAddress, amount, destinationAddress)
      await withdraw(
        withdrawToken.address, // token address to withdraw
        withdrawAmountBigInt, // amount to withdraw
        address // destination address (send to self)
      )
      
      console.log('🟢 [VaultsPage] Withdrawal successful!')
      
    } catch (err: any) {
      console.error('🔴 [VaultsPage] Withdrawal error:', err)
      toast.error(`Withdrawal failed: ${err.message || 'Unknown error'}`)
    }
  }

  const vaultsBreadcrumb = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Vaults', url: '/vaults' },
  ])

  if (!isConnected) {
    return (
      <>
        <SEO
          title="Crypto Vaults"
          description="Deposit Bitcoin, Ethereum, and other crypto assets as collateral. Secure smart contract vaults on Ethereum."
          canonical="/vaults"
          jsonLd={vaultsBreadcrumb}
          keywords="crypto vault, Bitcoin vault, Ethereum collateral, DeFi collateral, WETH deposit, WBTC deposit, self-custodial vault, Anvil protocol vault"
        />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-l">
              <div className="w-[96px] h-[96px]">
                <WalletConnectIllustration className="w-full h-full" />
              </div>
            </div>
            <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-s">
              Connect Your Wallet
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Please connect your wallet to create and manage vaults.
            </p>
            <Button size="lg" onClick={openConnectModal}>Connect Wallet</Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title="Crypto Vaults"
        description="Deposit Bitcoin, Ethereum, and other crypto assets as collateral. Secure smart contract vaults on Ethereum."
        canonical="/vaults"
        jsonLd={vaultsBreadcrumb}
        keywords="crypto vault, Bitcoin vault, Ethereum collateral, DeFi collateral, WETH deposit, WBTC deposit, cbBTC deposit, self-custodial vault, Anvil protocol vault, collateral management"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header - Bitcoin.com React CL Style */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-display font-black text-heading-xl uppercase text-text-primary mb-2">
                Vaults
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Manage your collateral vaults and deposits
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <Button 
                onClick={() => setShowVaultDetailsModal(true)} 
                variant="secondary"
                size="lg" 
                className="inline-flex items-center justify-center w-full sm:w-auto text-sm md:text-base py-2 md:py-3"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 inline-block">
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="hidden sm:inline">View Details</span>
                <span className="sm:hidden">Details</span>
              </Button>
              <Button 
                onClick={() => setShowDepositModal(true)} 
                size="lg" 
                className="inline-flex items-center justify-center w-full sm:w-auto text-sm md:text-base py-2 md:py-3"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 inline-block">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="hidden sm:inline">Make Deposit</span>
                <span className="sm:hidden">Deposit</span>
              </Button>
            </div>
          </div>

          {/* How It Works Flow */}
          <HowItWorksFlow 
            variant="compact" 
            progress={userProgress}
            showProgress={true}
            clickable={true}
            className="mb-8" 
          />

        {/* Collateral Account */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="spinner h-12 w-12" />
          </div>
        ) : hasAccount ? (
          <div className="grid gap-4">
            {/* Main Collateral Account Card */}
            <Card interactive>
              <CardContent>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-brand-orange/20 to-brand-blue/20 flex items-center justify-center flex-shrink-0">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-orange sm:w-8 sm:h-8">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-1">
                        Collateral Account
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-mono">
                        {formatAddress(address || '')}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    <Button 
                      size="sm" 
                      onClick={() => setShowDepositModal(true)}
                      className="flex-1 sm:flex-none text-xs sm:text-sm py-1.5 sm:py-2 px-3"
                    >
                      Deposit
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => setShowWithdrawModal(true)}
                      className="flex-1 sm:flex-none text-xs sm:text-sm py-1.5 sm:py-2 px-3"
                    >
                      Withdraw
                    </Button>
                    <Button 
                      size="sm" 
                      variant="default"
                      onClick={() => setSelectedVaultId(selectedVaultId === 0n ? null : 0n)}
                      className="w-full sm:w-auto text-xs sm:text-sm py-1.5 sm:py-2 px-3"
                    >
                      {selectedVaultId === 0n ? 'Hide' : 'Details'}
                    </Button>
                  </div>
                </div>
                
                {/* Expanded Account Details */}
                {selectedVaultId === 0n && (
                  <div className="mt-l pt-l border-t border-border-default space-y-l">
                    {/* Liquidation Risk Indicator */}
                    {hasActiveLOCs && totalCollateralValueUSD > 0 && (
                      <LiquidationRiskIndicator 
                        collateralValueUSD={totalCollateralValueUSD}
                        locValueUSD={estimatedLOCValueUSD}
                        showRecommendations={true}
                      />
                    )}
                    
                    <CollateralBalancesDisplay 
                      accountAddress={address!}
                      onDeposit={(token) => {
                        setSelectedToken(token)
                        setShowDepositModal(true)
                      }}
                      onWithdraw={(token) => {
                        setWithdrawToken(token)
                        setShowWithdrawModal(true)
                      }}
                    />
                    <TransactionHistory transactions={transactions} />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          ) : (
            <Card>
              <CardContent className="text-center py-16">
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-brand-orange/20 to-brand-blue/20 flex items-center justify-center">
                    <VaultIcon />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  No Deposits Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                  Make your first deposit to start using your collateral account for LOCs and loans
                </p>
                <div className="flex justify-center">
                  <Button 
                    onClick={() => setShowDepositModal(true)} 
                    size="lg" 
                    className="inline-flex items-center justify-center w-full sm:w-auto text-sm sm:text-base py-2 sm:py-3"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 inline-block">
                      <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span className="hidden sm:inline">Make Your First Deposit</span>
                    <span className="sm:hidden">Make Deposit</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Deposit Modal */}
          <Transition appear show={showDepositModal} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => !isPending && setShowDepositModal(false)}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                      <Dialog.Title
                        as="h3"
                        className="text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4"
                      >
                        Make Deposit
                      </Dialog.Title>
                      
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Deposit crypto assets to your self-custodial collateral account.
                        </p>
                        
                        {/* Step Indicator for ETH deposits */}
                        {selectedToken?.isNative && (
                          <StepIndicator
                            steps={[
                              {
                                number: 1,
                                title: 'Wrap ETH',
                                status: depositStep === 1 ? 'active' : depositStep > 1 ? 'completed' : 'pending',
                              },
                              {
                                number: 2,
                                title: 'Approve WETH',
                                status: depositStep === 2 ? 'active' : depositStep > 2 ? 'completed' : 'pending',
                              },
                              {
                                number: 3,
                                title: 'Deposit',
                                status: depositStep === 3 ? 'active' : 'pending',
                              },
                            ]}
                          />
                        )}
                        
                        {/* Token Selection with BTC History */}
                        <div className="space-y-2">
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <TokenSelector
                                value={selectedToken}
                                onChange={(token) => {
                                  // Check if BTC is selected
                                  if (token.symbol === 'BTC') {
                                    setShowDepositModal(false)
                                    setShowBTCDepositModal(true)
                                    return
                                  }
                                  setSelectedToken(token as Token)
                                  // Reset step when changing tokens
                                  setDepositStep(1)
                                }}
                                includeBTC={true}
                                showBalance={true}
                                label="Select Token"
                                disabled={isPending || isApproving || isWrapping}
                              />
                            </div>
                            {selectedToken && selectedToken.symbol === 'BTC' && (
                              <button
                                onClick={() => setShowBTCHistoryModal(true)}
                                className="mb-1 p-3 rounded-lg bg-gray-700 dark:bg-gray-600 hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors text-white"
                                title="View BTC Deposit History"
                              >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Amount Input */}
                        <AmountInput
                          value={depositAmount}
                          onChange={setDepositAmount}
                          token={selectedToken}
                          maxAmount={actualBalance}
                          label="Amount to Deposit"
                          placeholder="0.00"
                          showMaxButton={true}
                          disabled={isPending || isApproving || isWrapping || !selectedToken}
                        />
                        
                        {/* Current Step Info */}
                        {selectedToken?.isNative && depositStep === 1 && (
                          <InfoBox variant="info" title="Step 1: Wrap ETH to WETH">
                            <p className="text-sm">
                              First, we'll wrap your ETH into WETH (Wrapped ETH), which can be used as collateral.
                              This is a 1:1 conversion with no fees.
                            </p>
                          </InfoBox>
                        )}
                        
                        {selectedToken && !selectedToken.isNative && needsApproval && (
                          <InfoBox variant="info" title={`Approve ${selectedToken.symbol}`}>
                            <p className="text-sm">
                              Approve the CollateralVault contract to spend your {selectedToken.symbol} tokens.
                              This is a one-time approval for the amount you're depositing.
                            </p>
                          </InfoBox>
                        )}
                        
                        {selectedToken && !selectedToken.isNative && !needsApproval && depositAmount && (
                          <InfoBox variant="success" title={`Deposit ${selectedToken.symbol} to Vault`}>
                            <p className="text-sm">
                              Deposit your {selectedToken.symbol} to your collateral account.
                              You can then use it for LOCs and loans.
                            </p>
                          </InfoBox>
                        )}
                        
                        {/* Info Box */}
                        {!selectedToken && (
                          <InfoBox variant="info" title="About Your Collateral Account">
                            <div className="space-y-2 text-sm">
                              <p>Your first deposit creates your collateral account on Anvil Protocol.</p>
                              <p className="font-semibold">Supported tokens: ETH, USDC, wBTC, cbBTC, WETH, wstETH</p>
                              <ul className="list-disc pl-5 space-y-1 mt-2">
                                <li>Self-custodial - you maintain full control</li>
                                <li>Use as collateral for LOCs and loans</li>
                                <li>Withdraw anytime (small fee applies)</li>
                                <li>ETH is automatically wrapped to WETH</li>
                              </ul>
                            </div>
                          </InfoBox>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4">
                          <Button 
                            variant="default" 
                            onClick={() => setShowDepositModal(false)} 
                            disabled={isPending || isApproving}
                            className="w-full sm:w-auto order-2 sm:order-1"
                          >
                            Cancel
                          </Button>
                          
                          {/* Smart Button based on current step */}
                          {selectedToken?.isNative && depositStep === 1 ? (
                            // Step 1: Wrap ETH
                            <Button 
                              onClick={handleDeposit} 
                              loading={isWrapping}
                              disabled={isWrapping || !depositAmount}
                              className="w-full sm:w-auto order-1 sm:order-2"
                            >
                              {isWrapping ? 'Wrapping...' : 'Wrap ETH'}
                            </Button>
                          ) : isAllowanceLoading && !hasApprovedCurrentAmount ? (
                            // Loading allowance from chain
                            <Button 
                              loading={true}
                              disabled={true}
                              className="w-full sm:w-auto order-1 sm:order-2"
                            >
                              Checking approval...
                            </Button>
                          ) : needsApproval ? (
                            // Step 2: Approve (show whenever approval is needed, regardless of depositStep)
                            <Button 
                              onClick={handleApprove} 
                              loading={isApproving}
                              disabled={isApproving || !selectedToken || !depositAmount}
                              className="w-full sm:w-auto order-1 sm:order-2"
                            >
                              {isApproving ? 'Approving...' : `Approve ${selectedToken?.symbol || ''}`}
                            </Button>
                          ) : (
                            // Step 3: Deposit (approval already granted or not needed)
                            <Button 
                              onClick={handleDeposit} 
                              loading={isPending}
                              disabled={isPending || !selectedToken || !depositAmount}
                              className="w-full sm:w-auto order-1 sm:order-2"
                            >
                              {isPending ? 'Depositing...' : 'Deposit'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
          
          {/* Withdraw Modal */}
          <Transition appear show={showWithdrawModal} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setShowWithdrawModal(false)}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                      <Dialog.Title
                        as="h3"
                        className="text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4"
                      >
                        Withdraw Funds
                      </Dialog.Title>
                      
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Withdraw crypto assets from your collateral account.
                        </p>
                        
                        {/* Active LOCs Warning */}
                        {hasActiveLOCs && (
                          <InfoBox variant="warning" title="Active LOCs Detected">
                            <div className="space-y-2 text-sm">
                              <p>
                                You have <strong>{locEvents?.length || 0} active Letter{locEvents?.length !== 1 ? 's' : ''} of Credit</strong> backed by your collateral.
                              </p>
                              <p className="font-semibold text-brand-orange">
                                ⚠️ We recommend closing your LOCs before withdrawing funds.
                              </p>
                              <p className="text-xs mt-1">
                                Withdrawing collateral may increase liquidation risk and could result in position closure.
                              </p>
                            </div>
                          </InfoBox>
                        )}
                        
                        {/* Token Selector */}
                        <TokenSelector
                          value={withdrawToken}
                          onChange={(token) => setWithdrawToken(token as Token)}
                          showBalance={false}
                          label="Select Token to Withdraw"
                          disabled={isWithdrawPending}
                        />
                        
                        {/* Amount Input */}
                        <AmountInput
                          value={withdrawAmount}
                          onChange={setWithdrawAmount}
                          token={withdrawToken}
                          maxAmount={withdrawAvailableBalance || 0n}
                          label="Amount to Withdraw"
                          placeholder="0.00"
                          showMaxButton={true}
                          disabled={isWithdrawPending || !withdrawToken}
                        />
                        
                        {/* Fee Display — 4a */}
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="text-text-secondary">Bitcoin.com fee</span>
                            <span className="font-semibold text-text-primary">
                              150 bps (1.5%)
                            </span>
                          </div>
                          <p className="text-xs text-text-secondary mt-1">
                            Applied on LOC issuance — not on deposits or withdrawals.
                          </p>
                        </div>

                        {/* Liquidation Risk Visualization */}
                        {hasActiveLOCs && withdrawToken && withdrawAmount && prices && (() => {
                          const tokenPrice = prices?.[withdrawToken.symbol]
                          if (!tokenPrice || tokenPrice <= 0) return null // Can't calculate without price
                          const withdrawValueUSD = parseFloat(withdrawAmount) * tokenPrice
                          const currentCollateralUSD = totalCollateralValueUSD
                          const postWithdrawCollateralUSD = currentCollateralUSD - withdrawValueUSD

                          // Use actual LOC value from events (not a hardcoded 50% assumption)
                          const actualLOCValueUSD = estimatedLOCValueUSD
                          const currentLTV = currentCollateralUSD > 0 ? (actualLOCValueUSD / currentCollateralUSD) * 100 : 0
                          // If collateral goes to 0 with active LOCs, user is insolvent (Infinity LTV)
                          const postWithdrawLTV = postWithdrawCollateralUSD > 0 ? (actualLOCValueUSD / postWithdrawCollateralUSD) * 100 : (actualLOCValueUSD > 0 ? 999 : 0)
                          
                          const isHighRisk = postWithdrawLTV > 75
                          const isMediumRisk = postWithdrawLTV > 50 && postWithdrawLTV <= 75
                          
                          const riskColor = isHighRisk ? 'text-red-600 dark:text-red-400' : isMediumRisk ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
                          const riskBgColor = isHighRisk ? 'bg-red-50 dark:bg-red-900/20 border-red-500' : isMediumRisk ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500' : 'bg-green-50 dark:bg-green-900/20 border-green-500'
                          const riskLabel = isHighRisk ? 'HIGH RISK' : isMediumRisk ? 'MEDIUM RISK' : 'LOW RISK'
                          
                          return (
                            <div className={`p-4 rounded-lg border-2 ${riskBgColor}`}>
                              <h4 className="text-sm font-semibold text-text-primary mb-3 uppercase tracking-wide">
                                Liquidation Risk Impact
                              </h4>
                              
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-text-secondary">Current Collateral:</span>
                                  <span className="font-semibold text-text-primary">${currentCollateralUSD.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-text-secondary">After Withdrawal:</span>
                                  <span className="font-semibold text-text-primary">${postWithdrawCollateralUSD.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-text-secondary">Withdrawing:</span>
                                  <span className="font-semibold text-brand-orange">-${withdrawValueUSD.toFixed(2)}</span>
                                </div>
                                
                                <div className="border-t pt-2 mt-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-text-secondary">Current LTV:</span>
                                    <span className="font-semibold">{currentLTV.toFixed(1)}%</span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-text-secondary">Post-Withdrawal LTV:</span>
                                    <span className={`font-bold ${riskColor}`}>{postWithdrawLTV.toFixed(1)}%</span>
                                  </div>
                                </div>
                                
                                <div className={`mt-3 p-2 rounded text-center font-bold ${riskColor}`}>
                                  {riskLabel}
                                </div>
                                
                                {isHighRisk && (
                                  <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-semibold">
                                    ⚠️ DANGER: This withdrawal brings you dangerously close to liquidation. Consider reducing the amount or closing LOCs first.
                                  </p>
                                )}
                                {isMediumRisk && (
                                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                                    ⚠️ CAUTION: This withdrawal increases your liquidation risk. Monitor your position carefully.
                                  </p>
                                )}
                              </div>
                            </div>
                          )
                        })()}
                        
                        {/* Confirmation Input for Active LOCs */}
                        {hasActiveLOCs && (
                          <div>
                            <Input
                              label='Type "I agree" to confirm withdrawal'
                              type="text"
                              placeholder="I agree"
                              value={withdrawConfirmation}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWithdrawConfirmation(e.target.value)}
                              disabled={isWithdrawPending}
                            />
                            <p className="mt-1 text-xs text-text-secondary">
                              By typing "I agree", you acknowledge the risks and confirm this withdrawal.
                            </p>
                          </div>
                        )}
                        
                        {/* Info Box */}
                        {!hasActiveLOCs && withdrawToken && (
                          <InfoBox variant="info" title="Safe to Withdraw">
                            <p className="text-sm">
                              You have no active LOCs. Withdrawing will not affect any positions.
                            </p>
                          </InfoBox>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4">
                          <Button 
                            variant="default" 
                            onClick={() => setShowWithdrawModal(false)} 
                            disabled={isWithdrawPending}
                            className="w-full sm:w-auto order-2 sm:order-1"
                          >
                            Cancel
                          </Button>
                          
                          <Button 
                            onClick={handleWithdraw} 
                            loading={isWithdrawPending}
                            disabled={
                              isWithdrawPending || 
                              !withdrawToken || 
                              !withdrawAmount ||
                              (hasActiveLOCs && withdrawConfirmation.trim().toLowerCase() !== 'i agree')
                            }
                            className="w-full sm:w-auto order-1 sm:order-2"
                          >
                            {isWithdrawPending ? 'Withdrawing...' : 'Withdraw'}
                          </Button>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
          
          {/* Success Modal */}
          <Transition appear show={showSuccessModal} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setShowSuccessModal(false)}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                      {/* Success Icon */}
                      <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      
                      <Dialog.Title
                        as="h3"
                        className="text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4 text-center"
                      >
                        Deposit Successful!
                      </Dialog.Title>
                      
                      <div className="space-y-4">
                        <p className="text-center text-gray-600 dark:text-gray-400">
                          You've successfully deposited <strong>{depositedAmount} {depositedTokenSymbol}</strong> to your collateral account.
                        </p>
                        
                        <InfoBox variant="success" title="What's Next?">
                          <div className="space-y-2 text-sm">
                            <p><strong>You can now:</strong></p>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Issue Letters of Credit (LOCs) backed by your collateral</li>
                              <li>Request loans using your deposited assets</li>
                              <li>Deposit more assets to increase your borrowing power</li>
                              <li>Withdraw available funds anytime</li>
                            </ul>
                          </div>
                        </InfoBox>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4">
                          <Button 
                            variant="default" 
                            onClick={() => setShowSuccessModal(false)}
                            className="flex-1"
                          >
                            View Dashboard
                          </Button>
                          <Button 
                            onClick={() => {
                              setShowSuccessModal(false)
                              window.location.href = '/locs'
                            }}
                            className="flex-1"
                          >
                            Issue LOC
                          </Button>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
        </div>
      </div>

      {/* BTC Deposit Modal */}
      <BTCDepositModal
        isOpen={showBTCDepositModal}
        onClose={() => setShowBTCDepositModal(false)}
        onSuccess={() => {
          setShowBTCDepositModal(false)
          toast.success('BTC deposit completed! It may take a few minutes to appear in your vault.')
        }}
      />

      {/* BTC Deposit History Modal */}
      <Transition appear show={showBTCHistoryModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowBTCHistoryModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    BTC Deposit History
                  </Dialog.Title>

                  {address ? (
                    <BTCDepositHistory />
                  ) : (
                    <p className="text-gray-600 dark:text-gray-400">Connect your wallet to view history</p>
                  )}

                  <div className="mt-6 flex justify-end">
                    <Button onClick={() => setShowBTCHistoryModal(false)} variant="secondary">
                      Close
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Vault Details Modal */}
      <VaultDetailsModal
        isOpen={showVaultDetailsModal}
        onClose={() => setShowVaultDetailsModal(false)}
      />
    </>
  )
}

/**
 * Component to display collateral balances for all supported tokens
 */
function CollateralBalancesDisplay({ accountAddress, onDeposit, onWithdraw }: { 
  accountAddress: `0x${string}`
  onDeposit?: (token: Token) => void
  onWithdraw?: (token: Token) => void
}) {
  // Filter out native ETH (only show wrapped versions)
  const depositableTokens = Object.values(SUPPORTED_TOKENS).filter(t => !t.isNative)
  
  // Fetch real prices
  const { data: prices } = useTokenPrices(depositableTokens)
  
  const tokenBalances = depositableTokens.map(token => {
    const { availableBalance, reservedBalance, isLoading } = useAccountCollateralBalance(
      accountAddress,
      token.address
    )
    
    const totalBalance = (availableBalance || 0n) + (reservedBalance || 0n)
    const formattedBalance = formatTokenAmount(totalBalance, token.decimals)
    const price = prices?.[token.symbol] || 0
    const valueUSD = parseFloat(formattedBalance) * price
    
    return {
      token,
      totalBalance,
      availableBalance: availableBalance || 0n,
      reservedBalance: reservedBalance || 0n,
      formattedBalance,
      price,
      valueUSD,
      isLoading,
      hasBalance: totalBalance > 0n
    }
  })
  
  // Filter to only show tokens with balance
  const assetsWithBalance = tokenBalances.filter(b => b.hasBalance)
  
  // Calculate total value in USD
  const totalValueUSD = assetsWithBalance.reduce((sum, b) => sum + b.valueUSD, 0)
  
  if (assetsWithBalance.length === 0) {
    return (
      <div className="text-center py-8 text-text-secondary">
        <p>No collateral deposited yet</p>
        <p className="text-sm mt-2">Make your first deposit to get started</p>
      </div>
    )
  }
  
  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-2">
        <h3 className="text-lg font-bold text-text-primary">Your Collateral Assets</h3>
        <div className="text-right">
          <div className="text-sm text-text-secondary">Total Value</div>
          <div className="text-xl font-bold text-text-primary">${totalValueUSD.toFixed(2)}</div>
        </div>
      </div>
      
      {/* Assets List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-border-default overflow-hidden">
        {assetsWithBalance.map((asset, index) => (
          <AssetListItem
            key={asset.token.symbol}
            assetSymbol={asset.token.symbol}
            assetName={asset.token.name}
            balance={`${asset.formattedBalance} ${asset.token.symbol}`}
            valueUSD={asset.valueUSD.toFixed(2)}
            pricePerToken={`$${asset.price.toLocaleString()} per ${asset.token.symbol}`}
            actions={
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={() => onDeposit?.(asset.token)}
                  className="text-xs"
                >
                  Deposit
                </Button>
                <Button 
                  size="sm" 
                  variant="default"
                  onClick={() => onWithdraw?.(asset.token)}
                  disabled={asset.availableBalance === 0n}
                  className="text-xs"
                >
                  Withdraw
                </Button>
              </div>
            }
            showDivider={index < assetsWithBalance.length - 1}
          />
        ))}
      </div>
      
      {/* Minimum deposit warning */}
      {totalValueUSD < MINIMUM_COLLATERAL_USD && (
        <InfoBox variant="warning" title="Minimum Deposit Required">
          <p className="text-sm">
            You need at least <strong>${MINIMUM_COLLATERAL_USD.toFixed(2)}</strong> in collateral to request a loan. 
            Current balance: <strong>${totalValueUSD.toFixed(2)}</strong>
          </p>
          <p className="text-sm mt-2">
            Deposit <strong>${(MINIMUM_COLLATERAL_USD - totalValueUSD).toFixed(2)}</strong> more to get started with loans.
          </p>
        </InfoBox>
      )}

    </div>
  )
}

